import { useAppDispatch, useAppSelector } from "#store";
import { useDebouncedValue } from "rooks";
import {
    setColumnFav,
    setComparisonWeight,
    setFavoriteWeight,
    setQuery as setStoreQuery,
    setQueryWeight,
    setRelevanceWeight,
} from "#store/ui";
import React from "react";
import {
    Box,
    Button,
    Chip,
    FormLabel,
    IconButton,
    Slider,
    TextField,
    Tooltip,
} from "@mui/material";
import {
    Delete,
    UnfoldLessOutlined,
    UnfoldMoreOutlined,
} from "@mui/icons-material";
import { tokenize } from "#/util/stringUtils";

export const ReferenceQueryInput = () => {
    const storeQuery = useAppSelector(state => state.uiState.query);
    const [query, setQueryState] = React.useState(storeQuery);

    const dispatch = useAppDispatch();

    React.useEffect(() => {
        console.log("effect", storeQuery);
        setQueryState(storeQuery);
    }, [storeQuery]);

    React.useEffect(() => {
        dispatch(setStoreQuery(query));
    }, [dispatch, query]);

    return (
        <TextField
            aria-label="Reference Query"
            label="Reference Query"
            variant="standard"
            fullWidth
            value={query}
            onChange={evt => setQueryState(evt.target.value)}
            InputLabelProps={{
                shrink: true,
            }}
        />
    );
};

export const QueryState = () => {
    const storeSliderValue = useAppSelector(
        state => state.uiState.doiWeights[2]
    );
    const [sliderValue, setSliderValue] = React.useState(storeSliderValue);

    const dispatch = useAppDispatch();

    const debouncedSliderValue = useDebouncedValue(sliderValue, 250);

    React.useEffect(() => {
        console.log("effect", debouncedSliderValue[0]);
        dispatch(setQueryWeight(debouncedSliderValue[0] ?? 0.2));
    }, [debouncedSliderValue[0]]);

    return (
        <>
            <Tooltip
                title={
                    "Evaluates the query against the column headers. The more words in common, the higher a columns relevance."
                }
            >
                <FormLabel>Query Overlap</FormLabel>
            </Tooltip>
            <ReferenceQueryInput />
            <Slider
                aria-label="Query weight"
                value={sliderValue}
                valueLabelDisplay="auto"
                step={0.01}
                min={0}
                max={1}
                onChange={(evt, value) => {
                    if (typeof value === "number") {
                        setSliderValue(value);
                    } else {
                        setSliderValue(value[0]);
                    }
                }}
            />
        </>
    );
};

export const FavoriteColumnBox = () => {
    const dispatch = useAppDispatch();

    const favoriteColumns = useAppSelector(state => {
        return state.uiState.favoriteColumns;
    });

    const [visible, setVisible] = React.useState(5);

    const showMoreFavs = () => {
        setVisible(vis => Math.min(favoriteColumns.length, vis + 5));
    };
    const showLessFavs = () => {
        setVisible(vis => Math.max(0, vis - 5));
    };

    if (favoriteColumns.length > 0) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    alignItems: "center",
                }}
            >
                <Box sx={{ flexGrow: 1 }}>
                    {favoriteColumns
                        .map(fc => {
                            return (
                                <Chip
                                    key={fc}
                                    label={tokenize(fc).join(" ")}
                                    sx={{ margin: "0px 5px" }}
                                    size={"small"}
                                    onDelete={() =>
                                        dispatch(setColumnFav(fc, false))
                                    }
                                />
                            );
                        })
                        .filter((x, i) => i <= visible)}
                    {visible <= favoriteColumns.length ? (
                        <IconButton
                            size={"small"}
                            onClick={() => showMoreFavs()}
                        >
                            <UnfoldMoreOutlined />
                        </IconButton>
                    ) : null}
                    {visible >= Math.min(favoriteColumns.length, 5) ? (
                        <IconButton
                            size={"small"}
                            onClick={() => showLessFavs()}
                        >
                            <UnfoldLessOutlined />
                        </IconButton>
                    ) : null}
                </Box>
                <Tooltip title="Clear all Favorites">
                    <IconButton
                        size="small"
                        disabled={favoriteColumns.length === 0}
                        onClick={() =>
                            favoriteColumns.forEach(d =>
                                dispatch(setColumnFav(d, false))
                            )
                        }
                    >
                        <Delete />
                    </IconButton>
                </Tooltip>
            </Box>
        );
    } else {
        return null;
    }
};

export const FavoriteColumnsState = () => {
    const storeSliderValue = useAppSelector(
        state => state.uiState.doiWeights[3]
    );
    const [sliderValue, setSliderValue] = React.useState(storeSliderValue);

    const dispatch = useAppDispatch();

    const debouncedSliderValue = useDebouncedValue(sliderValue, 250);

    React.useEffect(() => {
        console.log("effect", debouncedSliderValue[0]);
        dispatch(setFavoriteWeight(debouncedSliderValue[0] ?? 0.2));
    }, [debouncedSliderValue[0]]);

    return (
        <>
            <Tooltip
                title={
                    "Allows to manipulate the influence of favorited columns to the column relevance."
                }
            >
                <FormLabel>Favorites</FormLabel>
            </Tooltip>
            <FavoriteColumnBox />
            <Slider
                aria-label="Favorite weight"
                value={sliderValue}
                valueLabelDisplay="auto"
                step={0.01}
                min={0}
                max={1}
                onChange={(evt, value) => {
                    if (typeof value === "number") {
                        setSliderValue(value);
                    } else {
                        setSliderValue(value[0]);
                    }
                }}
            />
        </>
    );
};

export const RelevancyColumnsState = () => {
    const dispatch = useAppDispatch();

    const storeSliderValue = useAppSelector(
        state => state.uiState.doiWeights[0]
    );
    const [sliderValue, setSliderValue] = React.useState(storeSliderValue);

    const debouncedSliderValue = useDebouncedValue(sliderValue, 250);

    React.useEffect(() => {
        console.log("effect", debouncedSliderValue[0]);
        dispatch(setRelevanceWeight(debouncedSliderValue[0] ?? 0.2));
    }, [debouncedSliderValue[0]]);

    return (
        <>
            <Tooltip
                title={
                    "Relevance is based on the entropy of the columns column."
                }
            >
                <FormLabel>Relevancy</FormLabel>
            </Tooltip>
            <Slider
                aria-label="Relevancy weight"
                value={sliderValue}
                valueLabelDisplay="auto"
                step={0.01}
                min={0}
                max={1}
                onChange={(evt, value) => {
                    if (typeof value === "number") {
                        setSliderValue(value);
                    } else {
                        setSliderValue(value[0]);
                    }
                }}
            />
        </>
    );
};

export const ComparisonColumnsState = () => {
    const dispatch = useAppDispatch();

    const storeSliderValue = useAppSelector(
        state => state.uiState.doiWeights[1]
    );
    const [sliderValue, setSliderValue] = React.useState(storeSliderValue);

    const debouncedSliderValue = useDebouncedValue(sliderValue, 250);

    React.useEffect(() => {
        console.log("effect", debouncedSliderValue[0]);
        dispatch(setComparisonWeight(debouncedSliderValue[0] ?? 0.2));
    }, [debouncedSliderValue[0]]);

    return (
        <>
            <Tooltip
                title={
                    "Compares the sets of values across columns with the same name (i.e. vertically). The more similar two table columns are, the less similar their relevance is."
                }
            >
                <FormLabel>Comparison</FormLabel>
            </Tooltip>
            <Slider
                aria-label="Comparison weight"
                value={sliderValue}
                valueLabelDisplay="auto"
                step={0.01}
                min={0}
                max={1}
                onChange={(evt, value) => {
                    if (typeof value === "number") {
                        setSliderValue(value);
                    } else {
                        setSliderValue(value[0]);
                    }
                }}
            />
        </>
    );
};
